import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {ActionButton, Button} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';
import EditIcon from '@edna/icons/edit.svg';
import {formatNumber} from '@edna/utils';

import {InlineLinkButton, SecondaryText, Text} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {EClientStatus, useClientStatus} from 'src/containers/ClientStatus';
import {useGetCurrentUserQuery} from 'src/containers/User';
import {CardIcon} from 'src/icons';
import {parseISO} from 'src/utils';

import {useGetPayKeeperAutopaymentInfoQuery} from '../../api';
import {TPayKeeperAutopymentInfo} from '../../definitions';
import * as S from '../../style';
import {useAutopaymentRecommendedThreshold} from '../useAutopaymentRecommendedThreshold';
import {
  showChangeAutopaymentCreditsModal,
  showChangeAutopaymentSumModal,
  showOffAutopaymentModal,
} from './Modals';

const SupportActions = React.memo(() => {
  const {t} = useTranslation();

  return (
    <Button onClick={showOffAutopaymentModal} size="S">
      {t(`BalanceReplenishment:actions.deleteAutopayment`)}
    </Button>
  );
});

const ClientActions = React.memo(() => {
  const {t} = useTranslation();

  const actionsOptions = React.useMemo(
    () => [
      {
        content: t('BalanceReplenishment:actions.changeAmount'),
        handler: showChangeAutopaymentSumModal,
      },
      {
        content: t('BalanceReplenishment:actions.changeCardCredits'),
        handler: showChangeAutopaymentCreditsModal,
      },
      {
        content: t('BalanceReplenishment:actions.deleteAutopayment'),
        handler: showOffAutopaymentModal,
      },
    ],
    [t],
  );

  return (
    <ActionButton options={actionsOptions} size="XS" appearance="tertiary">
      <EditIcon />
      {t('BalanceReplenishment:actions.changeAutopaymentSettings')}
    </ActionButton>
  );
});

const AutopaymentInfo = React.memo<{info: TPayKeeperAutopymentInfo}>(({info}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: user} = useGetCurrentUserQuery(undefined, {skip: !!supportTenantId});

  const recommendedThreshold = useAutopaymentRecommendedThreshold();
  const clientStatus = useClientStatus();

  const handleChangeSettings = React.useCallback(showChangeAutopaymentSumModal, []);

  const isAutopaymentOwner = user?.id === info.userId;

  return (
    <Stack size="2">
      <Stack size="2" direction="ROW" align="center">
        <Text weight={500} color="grey900">
          <S.SecondaryCoinsIcon />
          {t('BalanceReplenishment:autopaymentStatus.connected')}
        </Text>
        {!!supportTenantId && <SupportActions />}
        {!supportTenantId && isAutopaymentOwner && <ClientActions />}
      </Stack>
      <S.NestedBlock>
        {!!info.maskedCardNumber && (isAutopaymentOwner || !!supportTenantId) && (
          <Text color="grey400" size="XS">
            <Stack direction="ROW" align="center" size="2">
              <Icon as={CardIcon} size="16px" />
              {info.maskedCardNumber}
            </Stack>
          </Text>
        )}
        {info.recurrentPaymentSum < recommendedThreshold && (
          <Banner type="WARNING" shadowSize="XS" fontSize="S" noMargin>
            <Trans
              i18nKey={`BalanceReplenishment:payKeeperThresholdWarning.${isAutopaymentOwner ? 'owner' : 'others'}`}
            >
              <InlineLinkButton onClick={handleChangeSettings} />
            </Trans>
          </Banner>
        )}
        {!!info.errorDate && (
          <Banner
            type="ERROR"
            shadowSize="XS"
            fontSize="S"
            noMargin
            closeId="PAYKEEPER_AUTOPAYMENT_ERROR"
          >
            {t('BalanceReplenishment:errors.payKeeperAutopayment', {
              date: parseISO(info.errorDate, 'dd.MM.yyyy'),
            })}
          </Banner>
        )}
        {clientStatus === EClientStatus.INACTIVE && (
          <Banner
            type="ERROR"
            shadowSize="XS"
            fontSize="S"
            noMargin
            closeId="PAYKEEPER_AUTOPAYMENT_TENANT_BLOCKED"
          >
            {t('BalanceReplenishment:errors.payKeeperAutopaymentTenantBlocked')}
          </Banner>
        )}
        <SecondaryText size="S">
          <Trans
            i18nKey="BalanceReplenishment:payKeeperAutopaymentInfo"
            values={{amount: formatNumber(info.recurrentPaymentSum)}}
          >
            <Text color="blue500" isInline size="S" />
            <Text isInline size="S" />
          </Trans>
        </SecondaryText>
      </S.NestedBlock>
    </Stack>
  );
});

const PayKeeperAutopaymentInfo = React.memo(() => {
  const {id: supportTenantId} = useParams<'id'>();

  const {data: info} = useGetPayKeeperAutopaymentInfoQuery(supportTenantId);

  if (!info) {
    return null;
  }

  return <AutopaymentInfo info={info} />;
});

export {PayKeeperAutopaymentInfo};
